import { type FetchOptions, type FetchResponse, ofetch } from 'ofetch'

const AUTH_TOKEN_KEY = 'sdkjfdg2g8'

type ApiBody = BodyInit | Record<string, any> | null

/**
 * Base fetch class — the single source of truth for API calls in web-commerce.
 *
 * Wraps `ofetch` to provide:
 * - a fixed base URL
 * - the auth token from localStorage (client-side only)
 * - automatic JSON / FormData body serialization
 * - normalized error responses
 */
export class BaseApi {
  private client: ReturnType<typeof ofetch.create>

  constructor(baseURL: string) {
    this.client = ofetch.create({
      baseURL,

      onRequest({ options }) {
        const headers = new Headers(options.headers)

        // Attach the auth token (client-side only)
        if (import.meta.client) {
          const token = localStorage.getItem(AUTH_TOKEN_KEY)
          if (token) {
            headers.set('Authorization', `Bearer ${token}`)
          }
        }

        if (options.body instanceof FormData) {
          // Let the browser set multipart/form-data with the correct boundary.
          headers.delete('Content-Type')
        } else if (
          options.body &&
          typeof options.body === 'object' &&
          !(options.body instanceof Blob) &&
          !(options.body instanceof ArrayBuffer)
        ) {
          headers.set('Content-Type', 'application/json')
          if (typeof options.body !== 'string') {
            options.body = JSON.stringify(options.body)
          }
        }

        options.headers = headers
      },

      onResponseError({ response }) {
        BaseApi.handleError(response)
      },

      onRequestError({ error }) {
        console.error('[API Request Error]', error)
      },
    })
  }

  /** Centralized error handling */
  private static handleError(response: FetchResponse<unknown>) {
    const data = (response._data as Record<string, any>) || {}
    const meta = (data.meta as Record<string, any>) || {}

    const message =
      (meta.message as string) || (data.message as string) || response.statusText || 'خطای ناشناخته در ارتباط با سرور'

    console.error(`[API Error] ${response.status} ${response.url}: ${message}`, data)

    throw {
      status: response.status,
      data,
      meta,
      errors: (meta.errors as Record<string, string[]>) || {},
      message,
    }
  }

  async get<T>(url: string, options?: FetchOptions<'json'>): Promise<T> {
    return this.client<T>(url, { method: 'GET', ...options })
  }

  async post<T>(url: string, body?: ApiBody, options?: FetchOptions<'json'>): Promise<T> {
    return this.client<T>(url, { method: 'POST', body, ...options })
  }

  async put<T>(url: string, body?: ApiBody, options?: FetchOptions<'json'>): Promise<T> {
    return this.client<T>(url, { method: 'PUT', body, ...options })
  }

  async patch<T>(url: string, body?: ApiBody, options?: FetchOptions<'json'>): Promise<T> {
    return this.client<T>(url, { method: 'PATCH', body, ...options })
  }

  async delete<T>(url: string, options?: FetchOptions<'json'>): Promise<T> {
    return this.client<T>(url, { method: 'DELETE', ...options })
  }
}
